import React, { useState, useContext, useEffect } from 'react';
import { FaTimes, FaCloudUploadAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { Button, Form, Row, Col, Badge } from 'react-bootstrap';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import LocationPicker from './LocationPicker';

const PROVINCES = [
  'Gilgit-Baltistan', 'Khyber Pakhtunkhwa', 'Punjab',
  'Sindh', 'Balochistan', 'Azad Jammu & Kashmir',
];
const CLIMATES         = ['Cold', 'Mild', 'Hot', 'Variable'];
const SPOT_CATEGORIES  = ['Scenic','Historical','Adventure','Cultural','Religious','Wildlife','Other'];
const MAX_IMAGE_MB     = 5;
const MAX_GALLERY      = 10;
const MIN_GALLERY      = 5;
const ACCEPTED_MIME    = ['image/jpeg', 'image/png', 'image/webp'];
const AI_TIMEOUT_MS    = 120000; // 2 minutes — Gemini/n8n destination + spots generation can be slow

const isValidImage = (file) =>
  ACCEPTED_MIME.includes(file.type) && file.size <= MAX_IMAGE_MB * 1024 * 1024;

const blankSpot = () => ({
  _key            : Date.now() + Math.random(),
  spot_title      : '',
  spot_description: '',
  latitude        : '',
  longitude       : '',
  category        : '',
  visit_duration  : '',

  imageFile       : null,
  imagePreview    : null,
});

//  AI text-cleanup helpers 
// Gemini/n8n replies in plain sentences (e.g. "3 to 5 Days", "15,000 PKR").
// These small helpers turn that text into plain numbers/values the form needs.

function averageNumber(text) {
  const numbers = text ? text.match(/\d+/g) : null;
  if (!numbers) return '';
  const total = numbers.reduce((sum, n) => sum + Number(n), 0);
  return Math.round(total / numbers.length);
}

function numberOnly(text) {
  if (!text) return '';
  const digits = text.toString().replace(/[^0-9]/g, '');
  return digits ? Number(digits) : '';
}

function matchClimate(text) {
  if (!text) return '';
  const t = text.toLowerCase();
  const cold = t.includes('cold') || t.includes('snow');
  const hot  = t.includes('hot');
  const mild = t.includes('mild') || t.includes('pleasant');

  if (t.includes('variable') || (cold && (hot || mild))) return 'Variable';
  if (cold) return 'Cold';
  if (hot)  return 'Hot';
  if (mild) return 'Mild';
  return '';
}

function toMinutes(text) {
  const avg = averageNumber(text);
  if (avg === '') return '';
  const t = text.toLowerCase();
  if (t.includes('day'))  return avg * 24 * 60;
  if (t.includes('hour')) return avg * 60;
  return avg;
}

function matchSpotCategory(text) {
  if (!text) return 'Other';
  const t = text.toLowerCase();
  if (t.includes('histor') || t.includes('heritage'))         return 'Historical';
  if (t.includes('adventure') || t.includes('ski') || t.includes('hik')) return 'Adventure';
  if (t.includes('cultur'))                                    return 'Cultural';
  if (t.includes('religio') || t.includes('shrine') || t.includes('mosque')) return 'Religious';
  if (t.includes('wildlife') || t.includes('animal'))          return 'Wildlife';
  if (t.includes('nature') || t.includes('valley') || t.includes('lake') ||
      t.includes('park') || t.includes('scenic') || t.includes('view'))     return 'Scenic';
  return 'Other';
}

const AddEditDestinationForm = ({ show, onHide, destination = null }) => {
  const { baseUrl, authToken, setDestinations, showDestinationToast, setLoading } =
    useContext(AppContext);

  const isEditMode = Boolean(destination);

  const [step,         setStep]         = useState(1);
  const [submitting,   setSubmitting]   = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  const [destination_name, setDestinationName] = useState('');
  const [province,         setProvince]         = useState('');
  const [district,         setDistrict]         = useState('');
  const [nearest_city,     setNearestCity]      = useState('');
  const [latitude,         setLatitude]         = useState(''); 
  const [longitude,        setLongitude]        = useState('');  
  const [imageFile,        setImageFile]        = useState(null); 
  const [imagePreview,     setImagePreview]     = useState(null); 

  const [climate,               setClimate]              = useState(''); 
  const [best_season,           setBestSeason]           = useState('');  
  const [recommended_duration,  setRecommendedDuration]  = useState('');  
  const [min_budget,            setMinBudget]            = useState(''); 
  const [max_budget,            setMaxBudget]            = useState('');  
  const [description,           setDescription]          = useState(''); 

  const [tourist_spots,    setTouristSpots]    = useState([]);  
  const [galleryFiles,     setGalleryFiles]    = useState([]);  
  const [galleryPreviews,  setGalleryPreviews] = useState([]); 

  useEffect(() => {
    if (!show) return;

    if (isEditMode && destination) {
      setDestinationName   (destination.destination_name     ?? '');
      setProvince          (destination.province             ?? '');
      setDistrict          (destination.district             ?? '');
      setNearestCity       (destination.nearest_city         ?? '');
      setLatitude          (destination.latitude             ?? '');
      setLongitude         (destination.longitude            ?? '');
      setImageFile         (null);
      setImagePreview      (destination.image ? `http://127.0.0.1:8000/storage/${destination.image}` : null);
      setClimate           (destination.climate              ?? '');
      setBestSeason        (destination.best_season          ?? '');
      setRecommendedDuration(destination.recommended_duration ?? '');
      setMinBudget         (destination.min_budget           ?? '');
      setMaxBudget         (destination.max_budget           ?? '');
      setDescription       (destination.description          ?? '');
      setGalleryFiles   ([]);
      setGalleryPreviews(
        (destination.jjdestinationgallerys ?? []).map((g) =>
          g.des_gallery ? `http://127.0.0.1:8000/storage/${g.des_gallery}` : null
        ).filter(Boolean)
      );
      setTouristSpots(
        (destination.jjdestinationspots ?? []).map((s) => ({
          _key            : `db_${s.id}_${Date.now()}`,
          id              : s.id,
          spot_title      : s.spot_title       ?? '',
          spot_description: s.spot_description ?? '',
          latitude        : s.latitude         ?? '',
          longitude       : s.longitude        ?? '',
          category        : s.category         ?? '',
          visit_duration  : s.visit_duration   ?? '',
          imageFile       : null,
          imagePreview    : s.spot_image
            ? `http://127.0.0.1:8000/storage/${s.spot_image}`
            : null,
        }))
      );
    } else {
      setStep(1);
      setDestinationName('');   setProvince('');    setDistrict('');
      setNearestCity('');        setLatitude('');    setLongitude('');
      setImageFile(null);        setImagePreview(null);
      setClimate('');            setBestSeason('');
      setRecommendedDuration(''); setMinBudget(''); setMaxBudget('');
      setDescription('');
      setGalleryFiles([]);       setGalleryPreviews([]);
      setTouristSpots([]);
    }
  }, [show, destination]);

  const handleAutoFill = () => {
    if (!destination_name.trim()) {
      showDestinationToast('Pehle destination name likhein', 'error');
      return;
    }

    setAiGenerating(true);

    axios.post(
      `${baseUrl}/autofill`,
      { destination: destination_name },
      { timeout: AI_TIMEOUT_MS }
    )
      .then((res) => {
        setAiGenerating(false);

        if (!res.data.success) {
          showDestinationToast(res.data.message || 'AI generation failed', 'error');
          return;
        }

        const dest  = res.data.data.destination ?? {};
        const spots = res.data.data.spots ?? [];

        setDestinationName(dest.destination_name ?? destination_name);
        setProvince(dest.province ?? '');
        setDistrict(dest.district ?? '');
        setNearestCity(dest.nearest_city ?? '');
        setLatitude(dest.latitude ?? '');
        setLongitude(dest.longitude ?? '');

        setClimate(matchClimate(dest.climate));
        setBestSeason(dest.best_season ?? '');
        setDescription(dest.description ?? '');
        setMinBudget(numberOnly(dest.min_budget));
        setMaxBudget(numberOnly(dest.max_budget));
        setRecommendedDuration(averageNumber(dest.recommended_duration));

        // Tourist spots
        setTouristSpots(
          spots.map((spot) => ({
            _key            : Date.now() + Math.random(),
            spot_title      : spot.spot_title ?? '',
            spot_description: spot.spot_description ?? '',
            latitude        : spot.latitude ?? '',
            longitude       : spot.longitude ?? '',
            category        : matchSpotCategory(spot.category),
            visit_duration  : toMinutes(spot.visit_duration),
            imageFile       : null,
            imagePreview    : null,
          }))
        );

        showDestinationToast('AI ne fields fill kar di hain — review kar lein', 'success');
      })
      .catch((err) => {
        setAiGenerating(false);
        console.error('AutoFill error:', err);

        if (err.code === 'ECONNABORTED') {
          showDestinationToast('AI response me zyada time lag raha hai, dobara try karein', 'error');
        } else {
          showDestinationToast('AI se connect nahi ho saka', 'error');
        }
      });
  };

  const handlePrimaryImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isValidImage(file)) {
      showDestinationToast('JPG, PNG or WebP only — max 5 MB', 'error'); return;
    }
    const reader = new FileReader();
    reader.onloadend = () => { setImageFile(file); setImagePreview(reader.result); };
    reader.readAsDataURL(file);
  };

  const handleGalleryImages = (e) => {
    const selected = Array.from(e.target.files).filter(isValidImage);
    if (!selected.length) {
      showDestinationToast('No valid images — JPG, PNG, WebP, max 5 MB each', 'error'); return;
    }
    if (galleryPreviews.length + selected.length > MAX_GALLERY) {
      showDestinationToast(`Maximum ${MAX_GALLERY} gallery photos allowed`, 'error'); return;
    }
    selected.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryFiles   ((prev) => [...prev, file]);
        setGalleryPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryPhoto = (index) => {
    setGalleryFiles   ((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSpot = (key, field, value) =>
    setTouristSpots((prev) =>
      prev.map((s) => s._key === key ? { ...s, [field]: value } : s)
    );

  const handleSpotImage = (e, spotKey) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!isValidImage(file)) {
      showDestinationToast('JPG, PNG or WebP only — max 5 MB', 'error'); return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      updateSpot(spotKey, 'imageFile',    file);
      updateSpot(spotKey, 'imagePreview', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = () => {
    setLoading(true);
    setSubmitting(true);

    const fd = new FormData();

    fd.append('destination_name',     destination_name);
    fd.append('province',             province);
    fd.append('district',             district);
    fd.append('nearest_city',         nearest_city);
    fd.append('latitude',             latitude);
    fd.append('longitude',            longitude);
    fd.append('climate',              climate);
    fd.append('best_season',          best_season);
    fd.append('description',          description);
    fd.append('min_budget',           min_budget);
    fd.append('max_budget',           max_budget);
    fd.append('recommended_duration', recommended_duration);
    if (imageFile) fd.append('image', imageFile);

    galleryFiles.forEach((file, i) => fd.append(`gallery_images[${i}]`, file));

    fd.append('tourist_spots', JSON.stringify(
      tourist_spots.map((s) => ({
        spot_title      : s.spot_title,
        spot_description: s.spot_description,
        latitude        : s.latitude        !== '' ? s.latitude        : null,
        longitude       : s.longitude       !== '' ? s.longitude       : null,
        category        : s.category,
        visit_duration  : s.visit_duration  !== '' ? parseInt(s.visit_duration, 10) : null,
      }))
    ));

    tourist_spots.forEach((s, i) => {
      if (s.imageFile) fd.append(`spot_images[${i}]`, s.imageFile);
    });

    if (isEditMode) fd.append('_method', 'POST');

    const url = isEditMode
      ? `${baseUrl}/admin/update-destination/${destination.id}`
      : `${baseUrl}/admin/add-destination`;

    axios.post(url, fd, {
      headers: {
        Authorization : `Bearer ${authToken}`,
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((res) => {
        setLoading(false);
        setSubmitting(false);
        if (res.data.success) {
          if (isEditMode) {
            setDestinations((prev) =>
              prev.map((d) => d.id === destination.id ? { ...d, ...res.data.data } : d)
            );
            showDestinationToast('Destination updated successfully!', 'success');
          } else {
            setDestinations((prev) => [res.data.data, ...prev]);
            showDestinationToast('Destination added successfully!', 'success');
          }
          onHide();
        } else {
          showDestinationToast(res.data.message || 'Something went wrong', 'error');
        }
      })
      .catch((err) => {
        setLoading(false);
        setSubmitting(false);
        console.error('Destination save error:', err);
        showDestinationToast('Unable to connect to server', 'error');
      });
  };

  const step1Invalid = !destination_name.trim() || !province || !district.trim();
  const step2Invalid = !best_season.trim() || !recommended_duration.toString().trim();
  const step3Invalid =
    tourist_spots.some((s) => !s.spot_title.trim()) ||
    galleryPreviews.length < MIN_GALLERY;

  if (!show) return null;

  return (
    <div className="adm-modal-overlay" onClick={onHide}>
      <div className="adm-modal-container adm-add-destination-modal"
        onClick={(e) => e.stopPropagation()}>

        <div className="adm-modal-header">
          <div>
            <h2 className="adm-modal-title">
              {isEditMode ? 'Edit Destination' : 'Add New Destination'}
            </h2>
            <p className="adm-modal-subtitle">
              {isEditMode ? 'Update destination details' : 'Create a new travel destination'}
            </p>
          </div>
          <button className="adm-modal-close" onClick={onHide} disabled={submitting}>
            <FaTimes />
          </button>
        </div>

        <div className="adm-modal-body adm-destination-form">

          <div className="adm-step-indicator">
            {[1, 2, 3].map((n, i) => (
              <React.Fragment key={n}>
                {i > 0 && <div className={`adm-step-line ${step >= n ? 'active' : ''}`} />}
                <div className={`adm-step ${step >= n ? 'active' : ''}`}>
                  <span className="adm-step-number">{n}</span>
                  <span className="adm-step-label">
                    {n === 1 ? 'Basic Info' : n === 2 ? 'Details' : 'Spots & Photos'}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>

          {step === 1 && (
            <div className="adm-form-section">
              <h3 className="adm-form-section-title">Basic Information</h3>

              <Form.Group className="mb-3">
                <Form.Label>Destination Name <span className="text-danger">*</span></Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text" placeholder="e.g., Hunza Valley"
                    value={destination_name}
                    onChange={(e) => setDestinationName(e.target.value)}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={handleAutoFill}
                    disabled={aiGenerating || !destination_name.trim() || isEditMode}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {aiGenerating ? 'Generating...' : '✨ Generate AI'}
                  </Button>
                </div>
                {isEditMode && (
                  <small className="text-muted">Auto-fill sirf naye destinations ke liye hai</small>
                )}
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Province <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={province} onChange={(e) => setProvince(e.target.value)}>
                      <option value="">Select Province</option>
                      {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>District <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" placeholder="e.g., Hunza"
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Nearest City</Form.Label>
                <Form.Control type="text" placeholder="e.g., Karimabad"
                  value={nearest_city}
                  onChange={(e) => setNearestCity(e.target.value)} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Location on Map</Form.Label>
                <LocationPicker
                  latitude={latitude}
                  longitude={longitude}
                  searchQuery={destination_name}
                  onLocationChange={(lat, lng) => {
                    setLatitude(lat.toFixed(8));
                    setLongitude(lng.toFixed(8));
                  }}
                  onReverseGeocode={({ province: geoProvince, district: geoDistrict, nearest_city: geoCity }) => {
                    // Only auto-fill if admin hasn't already typed something never overwrite manual edits
                    if (geoProvince && PROVINCES.includes(geoProvince) && !province) {
                      setProvince(geoProvince);
                    }
                    if (geoDistrict && !district) {
                      setDistrict(geoDistrict);
                    }
                    if (geoCity && !nearest_city) {
                      setNearestCity(geoCity);
                    }
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Primary Image</Form.Label>
                <div className="adm-image-upload-area">
                  {imagePreview ? (
                    <div className="adm-image-preview">
                      <img src={imagePreview} alt="Primary"
                        style={{ maxHeight: 160, objectFit: 'cover', borderRadius: 8 }} />
                      <button type="button" className="adm-remove-inclusion"
                        onClick={() => { setImageFile(null); setImagePreview(null); }}>
                        <FaTrash /> Remove
                      </button>
                    </div>
                  ) : (
                    <label className="adm-upload-label">
                      <FaCloudUploadAlt className="adm-upload-icon" />
                      <span>Upload primary image</span>
                      <small>JPG, PNG, WebP — max {MAX_IMAGE_MB} MB</small>
                      <input type="file" accept="image/jpeg,image/png,image/webp"
                        style={{ display: 'none' }} onChange={handlePrimaryImage} />
                    </label>
                  )}
                </div>
              </Form.Group>
            </div>
          )}

          {step === 2 && (
            <div className="adm-form-section">
              <h3 className="adm-form-section-title">Description & Details</h3>

              <Form.Group className="mb-3">
                <Form.Label>Climate</Form.Label>
                <Form.Select value={climate} onChange={(e) => setClimate(e.target.value)}>
                  <option value="">Select Climate</option>
                  {CLIMATES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Best Season <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" placeholder="e.g., May to October"
                      value={best_season}
                      onChange={(e) => setBestSeason(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Recommended Duration <span className="text-danger">*</span>
                      <small className="text-muted ms-1">(days)</small>
                    </Form.Label>
                    <Form.Control
                      type="number" min="1" placeholder="e.g., 5"
                      value={recommended_duration}
                      onChange={(e) => setRecommendedDuration(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Min Budget (PKR)</Form.Label>
                    <Form.Control type="number" min="0" placeholder="e.g., 15000"
                      value={min_budget}
                      onChange={(e) => setMinBudget(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Max Budget (PKR)</Form.Label>
                    <Form.Control type="number" min="0" placeholder="e.g., 50000"
                      value={max_budget}
                      onChange={(e) => setMaxBudget(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={6} maxLength={500}
                  placeholder="Describe the destination..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)} />
                <small className="text-muted">{description.length} / 500 characters</small>
              </Form.Group>
            </div>
          )}

          {step === 3 && (
            <div className="adm-form-section">

              <div className="mb-4">
                <h3 className="adm-form-section-title">
                  Tourist Spots
                </h3>

                <Button variant="success" size="sm" className="mb-3"
                  onClick={() => setTouristSpots((prev) => [...prev, blankSpot()])}>
                  <FaPlus /> Add Spot
                </Button>

                {tourist_spots.length === 0 ? (
                  <div className="text-center p-4 border rounded bg-light">
                    <p className="text-muted mb-0">No tourist spots added yet.</p>
                  </div>
                ) : (
                  tourist_spots.map((spot, index) => (
                    <div key={spot._key} className="adm-spot-card mb-3 p-3 border rounded">

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Spot {index + 1}</h6>
                        <Button variant="danger" size="sm"
                          onClick={() => setTouristSpots((prev) =>
                            prev.filter((s) => s._key !== spot._key))}>
                          <FaTrash />
                        </Button>
                      </div>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                            <Form.Control type="text" placeholder="e.g., Attabad Lake"
                              value={spot.spot_title}
                              onChange={(e) => updateSpot(spot._key, 'spot_title', e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Category</Form.Label>
                            <Form.Select value={spot.category}
                              onChange={(e) => updateSpot(spot._key, 'category', e.target.value)}>
                              <option value="">Select Category</option>
                              {SPOT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-2">
                        <Form.Label>Description</Form.Label>
                        <Form.Control type="text" placeholder="Brief description"
                          value={spot.spot_description}
                          onChange={(e) => updateSpot(spot._key, 'spot_description', e.target.value)} />
                      </Form.Group>

                      <Row>
                        <Col md={8}>
                          <Form.Group className="mb-2">
                            <Form.Label>Spot Location on Map</Form.Label>
                            <LocationPicker
                              latitude={spot.latitude}
                              longitude={spot.longitude}
                              searchQuery={spot.spot_title}
                              height={220}
                              onLocationChange={(lat, lng) => {
                                updateSpot(spot._key, 'latitude', lat.toFixed(8));
                                updateSpot(spot._key, 'longitude', lng.toFixed(8));
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label>
                              Visit Duration
                              <small className="text-muted ms-1">(minutes)</small>
                            </Form.Label>
                            <Form.Control type="number" min="1" placeholder="e.g., 120"
                              value={spot.visit_duration}
                              onChange={(e) => updateSpot(spot._key, 'visit_duration', e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-2">
                        <Form.Label>Spot Image</Form.Label>
                        <div className="adm-image-upload-area small">
                          {spot.imagePreview ? (
                            <div className="adm-image-preview">
                              <img src={spot.imagePreview} alt="Spot"
                                style={{ maxHeight: 120, objectFit: 'cover', borderRadius: 6 }} />
                              <button type="button" className="adm-remove-inclusion"
                                onClick={() => {
                                  updateSpot(spot._key, 'imageFile',    null);
                                  updateSpot(spot._key, 'imagePreview', null);
                                }}>
                                <FaTrash /> Remove
                              </button>
                            </div>
                          ) : (
                            <label className="adm-upload-label">
                              <FaCloudUploadAlt className="adm-upload-icon" />
                              <span>Upload spot image</span>
                              <input type="file" accept="image/jpeg,image/png,image/webp"
                                style={{ display: 'none' }}
                                onChange={(e) => handleSpotImage(e, spot._key)} />
                            </label>
                          )}
                        </div>
                      </Form.Group>

                    </div>
                  ))
                )}
              </div>

              <div>
                <h3 className="adm-form-section-title">
                  Gallery Photos <span className="text-danger">({MIN_GALLERY}–{MAX_GALLERY} required)</span>
                </h3>
                <Badge bg={galleryPreviews.length < MIN_GALLERY ? 'warning' : 'success'}
                  className="mb-3 d-inline-block">
                  {galleryPreviews.length} / {MAX_GALLERY} photos
                  {galleryPreviews.length < MIN_GALLERY &&
                    ` — need ${MIN_GALLERY - galleryPreviews.length} more`}
                </Badge>

                <div className="adm-image-upload-area">
                  {galleryPreviews.length > 0 && (
                    <Row className="mb-3">
                      {galleryPreviews.map((preview, idx) => (
                        <Col xs={4} md={3} key={idx} className="mb-2">
                          <div className="position-relative">
                            <img src={preview} alt={`Gallery ${idx}`}
                              className="img-fluid rounded"
                              style={{ height: 100, width: '100%', objectFit: 'cover' }} />
                            <button type="button" className="adm-remove-inclusion"
                              onClick={() => removeGalleryPhoto(idx)}
                              style={{ position: 'absolute', top: 5, right: 5 }}>
                              <FaTrash />
                            </button>
                            {idx === 0 && (
                              <Badge bg="warning" text="dark"
                                className="position-absolute bottom-0 start-0 m-1">
                                Primary
                              </Badge>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                  {galleryPreviews.length < MAX_GALLERY && (
                    <label className="adm-upload-label large">
                      <FaCloudUploadAlt className="adm-upload-icon" />
                      <span>Click to upload destination photos</span>
                      <small>JPG, PNG, WebP — max {MAX_IMAGE_MB} MB each</small>
                      <input type="file" accept="image/jpeg,image/png,image/webp" multiple
                        style={{ display: 'none' }} onChange={handleGalleryImages} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="adm-modal-footer">
          <div className="d-flex justify-content-between w-100">
            <div>
              {step > 1 && (
                <Button variant="secondary" disabled={submitting}
                  onClick={() => setStep(step - 1)}>
                  ← Back
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              {step < 3 ? (
                <Button variant="primary"
                  onClick={() => setStep(step + 1)}
                  disabled={(step === 1 && step1Invalid) || (step === 2 && step2Invalid)}>
                  Next →
                </Button>
              ) : (
                <Button variant="primary" onClick={handleSubmit}
                  disabled={submitting || step3Invalid}>
                  {submitting
                    ? (isEditMode ? 'Saving...'    : 'Adding...')
                    : (isEditMode ? 'Save Changes' : 'Add Destination')}
                </Button>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AddEditDestinationForm;